import type { VoidFunctionComponent } from 'react';

import { useReportFlow } from '../hooks/useReportFlow';
import { useReportType } from '../hooks/useReportType';
import type { ReportHeading } from '../types';

import { reportHeadingsMap } from './common';

import styles from './Blurb.module.less';

type BlurbProps = {
  /**
   * A heading based on the report type.
   * If no report type is provided, a generic "Report" heading will be used.
   */
  reportTypeHeading: `${ReportHeading} Report` | 'Report';
};

const StandardBlurb: VoidFunctionComponent<BlurbProps> = ({ reportTypeHeading }) => (
  <>
    <p className={styles.paragraph}>
      The <span className="bold">Standard {reportTypeHeading}</span> uses FOSSA’s recommended filters to generate complete, ready-to-share reports in one click.
    </p>
    <p className={styles.paragraph}>
      Includes key dependency, license, and metadata details without requiring any configuration
      {' '}
      — perfect for teams who want reliable results, fast.
    </p>
  </>
);

const CustomBlurb: VoidFunctionComponent<BlurbProps> = ({ reportTypeHeading }) => (
  <>
    <p className={styles.paragraph}>
      The <span className="bold">Custom {reportTypeHeading}</span> gives you complete control over report content and structure.
    </p>
    <p className={styles.paragraph}>
      Customize included data, license sections, and dependency views directly in the preview
      {' '}
      to match your team’s workflow and compliance needs.
    </p>
  </>
);

/**
 * The blurb contains a short little paragraph which changes based on
 * the report flow (which is stored in the URL query params).
 */
export const Blurb: VoidFunctionComponent = () => {
  const [flow] = useReportFlow();
  const reportType = useReportType();
  const reportTypeHeading: BlurbProps['reportTypeHeading'] = reportHeadingsMap?.[reportType] ? `${reportHeadingsMap[reportType]} Report` : 'Report';
  //  This <div> is needed despite not having any styles/classes (it is a flex child).
  return (
    <div>
      <h3 className={styles.heading}>Report Details</h3>
      {flow === 'custom' ? (
        <CustomBlurb reportTypeHeading={reportTypeHeading} />
      ) : (
        <StandardBlurb reportTypeHeading={reportTypeHeading as `${ReportHeading} Report`} />
      )}
    </div>
  );
};
